import { useCallback, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AlertMessage } from '../components/alert-message';
import type { ShowDetailView__Show } from './ShowDetailView.generated';
import { Details, ViewZone } from './components';
import { Layout } from './components/layout';

interface ShowDetailViewProps {
  show: ShowDetailView__Show;
  showFolder?: { id: number; name: string };
}

export const ShowDetailView = ({ show, showFolder }: ShowDetailViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setViewId = useCallback<(viewId: number | undefined) => void>(
    (id: number | undefined) => {
      if (!id) return;
      setSearchParams(
        (prev) => {
          prev.set('viewId', String(id));
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const viewId = Number(searchParams.get('viewId')) || undefined;

  const defaultViewId = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set('viewId', String(show.defaultView.id));
    return params.toString();
  }, [searchParams, show.defaultView.id]);

  if (viewId == null) return <Navigate to={`?${defaultViewId}`} replace />;

  const view = show.views.find(({ id }) => id === viewId);
  if (view == null) return <Navigate to="/not-found" replace />;

  return (
    <Layout>
      <AlertMessage show={show} />
      <Details
        onViewChange={setViewId}
        show={show}
        showFolder={showFolder}
        view={view}
        viewId={viewId}
      />
      {view.viewZones.map((viewZone) => {
        const multiZone = view.viewZones.length > 1;
        return (
          <ViewZone
            id={`zone-${viewZone.zoneId}`}
            key={viewZone.id}
            layout={view.layout}
            multiZone={multiZone}
            show={show}
            viewZone={viewZone}
          />
        );
      })}
    </Layout>
  );
};
